import React from 'react';
import { Table, Tag, Space, Button, Tooltip, Typography, Popconfirm } from 'antd';
import { SettingOutlined, DeleteOutlined, WarningOutlined, CheckCircleOutlined, StopOutlined, MailOutlined } from '@ant-design/icons';
import { useTimeZone, getTimezoneByMode } from '../../../../contexts/TimeZoneContext';
import { convertFromUTC } from '../../../../utils/timezoneUtils';

const { Text } = Typography;

const formatAlertTime = (value, timeZoneMode, airportIata, airportsMap) => {
    if (!value) return '-';
    const zone = getTimezoneByMode(timeZoneMode, airportIata, airportsMap);
    const converted = convertFromUTC(value, zone);
    if (!converted || !converted.isValid()) return '—';
    return converted.format('YYYY-MM-DD HH:mm');
};

const UldStockMonitorTable = ({ loading, data, onEdit, onRemove }) => {
    const { timeZone, airportsMap, getTimeZoneDisplayName } = useTimeZone();

    const renderStatus = (status) => {
        if (status === 'WARNING') {
            return (
                <Tag color="error" icon={<WarningOutlined />}>
                    库存短缺
                </Tag>
            );
        }
        return (
            <Tag color="success" icon={<CheckCircleOutlined />}>
                正常
            </Tag>
        );
    };

    const columns = [
        {
            title: '机场代码',
            dataIndex: 'airport_iata',
            key: 'airport_iata',
            width: 100,
            render: (text) => <Text strong>{text}</Text>,
            sorter: (a, b) => (a.airport_iata || '').localeCompare(b.airport_iata || ''),
        },
        {
            title: '机场名称',
            dataIndex: 'airport_name',
            key: 'airport_name',
            ellipsis: true,
        },
        {
            title: '监控',
            key: 'monitor_state',
            width: 100,
            align: 'center',
            render: (_, record) => (
                record.config_id ? <Tag color="processing">已配置</Tag> : <Tag>未配置</Tag>
            ),
            filters: [
                { text: '已配置', value: 'configured' },
                { text: '未配置', value: 'unconfigured' },
            ],
            onFilter: (value, record) => (value === 'configured' ? Boolean(record.config_id) : !record.config_id),
        },
        {
            title: '当前可用库存',
            dataIndex: 'current_stock',
            key: 'current_stock',
            width: 120,
            align: 'center',
            render: (text, record) => {
                const isWarning = record.alert_status === 'WARNING';
                return (
                    <Text
                        strong
                        style={{
                            color: isWarning ? '#cf1322' : '#3f8600',
                            fontSize: '16px'
                        }}
                    >
                        {text}
                    </Text>
                );
            },
            sorter: (a, b) => a.current_stock - b.current_stock,
        },
        {
            title: '最低库存阈值',
            dataIndex: 'min_stock_threshold',
            key: 'min_stock_threshold',
            width: 120,
            align: 'center',
            render: (text) => text === 0 ? <Text type="secondary">未设置</Text> : text,
        },
        {
            title: '状态',
            dataIndex: 'alert_status',
            key: 'alert_status',
            width: 120,
            align: 'center',
            render: (text) => renderStatus(text),
            filters: [
                { text: '正常', value: 'NORMAL' },
                { text: '库存短缺', value: 'WARNING' },
            ],
            onFilter: (value, record) => record.alert_status === value,
        },
        {
            title: '邮件预警',
            dataIndex: 'is_email_enabled',
            key: 'is_email_enabled',
            width: 100,
            align: 'center',
            render: (enabled, record) => {
                if (enabled && record.min_stock_threshold > 0) {
                    return (
                        <Tooltip title="已开启邮件预警">
                            <MailOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                        </Tooltip>
                    );
                }
                if (enabled) {
                    return (
                        <Tooltip title="邮件开关已打开，但最低库存是 0，不会发信">
                            <MailOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title="邮件预警已关闭">
                        <StopOutlined style={{ color: '#d9d9d9', fontSize: '16px' }} />
                    </Tooltip>
                );
            }
        },
        {
            title: (
                <Tooltip title="跟随页面右上角的时区。选择当地时间时，按这个机场自己的时区显示。">
                    {`最近预警时间（${getTimeZoneDisplayName()}）`}
                </Tooltip>
            ),
            dataIndex: 'last_alert_at',
            key: 'last_alert_at',
            width: 220,
            render: (text, record) => formatAlertTime(text, timeZone, record.airport_iata, airportsMap),
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title={record.config_id ? '编辑配置' : '添加配置'}>
                        <Button
                            type="text"
                            icon={<SettingOutlined />}
                            onClick={() => onEdit(record)}
                            style={{ color: '#1890ff' }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="从列表移除这个机场？"
                        description="移除后不再显示，也不会再发预警邮件。之后可以重新添加。已填写的阈值和收件人会保留，但邮件开关会关闭。"
                        onConfirm={() => onRemove(record)}
                        okText="移除"
                        cancelText="取消"
                    >
                        <Tooltip title="从列表移除">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey="airport_iata"
            pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 个站点`,
            }}
            rowClassName={(record) => record.alert_status === 'WARNING' ? 'uld-stock-alert-warning-row' : ''}
        />
    );
};

export default UldStockMonitorTable;
